/**
 * Entry-side stand-ins for the console-only components (ADR-WES1).
 *
 * App.tsx renders these exactly like the real components. The real ones live
 * in `./console-components`, a chunk that `loadConsoleSurface` starts together
 * with ConsoleSurface (and the boot preload starts for a signed-in visit). Once
 * it is loaded a stand-in renders the real component synchronously, with no
 * extra Suspense fallback. Rendered before that (the legal gate, the outermost
 * console component, when the chunk is still on the way), a stand-in suspends
 * like any lazy component, under the loading screen ConsoleSurface already
 * showed, and keeps that choice for its lifetime, so an instance never switches
 * element type and remounts.
 *
 * The admin and partner bells are separate chunks: a provider session never
 * downloads them. They sit in their own Suspense boundary with a same-size
 * placeholder so the top bar does not shift or fall back to a page loader.
 */
import { lazy, Suspense, useState, type ComponentProps, type ComponentType } from "react";

import { requireMessageParts } from "../i18n";
import type { AdminNotificationsBell as AdminBellComponent } from "../components/AdminNotificationsBell";
import type { PartnerAnnouncementsBell as PartnerBellComponent } from "../components/PartnerAnnouncementsBell";
import type { ConsoleComponents } from "./console-components";

let loaded: ConsoleComponents | null = null;
let loading: Promise<ConsoleComponents> | null = null;

/** Load (once) the console component chunk. A failed load can be retried. */
export function loadConsoleComponents(): Promise<ConsoleComponents> {
  loading ??= import("./console-components").then(
    (module) => (loaded = module.consoleComponents),
    (error: unknown) => {
      loading = null;
      throw error;
    }
  );
  return loading;
}

/** Test seam: whether the console chunk has been evaluated. */
export function consoleComponentsLoaded(): boolean {
  return loaded !== null;
}

function consoleComponent<P extends object>(pick: (components: ConsoleComponents) => ComponentType<P>) {
  const Lazy = lazy(() => loadConsoleComponents().then((components) => ({ default: pick(components) })));
  function ConsoleComponent(props: P) {
    const [Component] = useState<ComponentType<P>>(() => (loaded ? pick(loaded) : (Lazy as ComponentType<P>)));
    return <Component {...props} />;
  }
  return ConsoleComponent;
}

export const AccessDashboard = consoleComponent((components) => components.AccessDashboard);
export const DashboardHelpStrip = consoleComponent((components) => components.DashboardHelpStrip);
export const DashboardRefreshControls = consoleComponent((components) => components.DashboardRefreshControls);
export const FeedbackDialog = consoleComponent((components) => components.FeedbackDialog);
export const LegalAcceptanceGate = consoleComponent((components) => components.LegalAcceptanceGate);
export const ProviderNotificationsBell = consoleComponent((components) => components.ProviderNotificationsBell);
export const StitchErrorBoundary = consoleComponent((components) => components.StitchErrorBoundary);

function BellPlaceholder() {
  return (
    <div aria-hidden="true" className="stitch-notifications-bell" data-testid="bell-placeholder">
      <span className="stitch-icon-button" />
    </div>
  );
}

const AdminBell = lazy(() =>
  Promise.all([import("../components/AdminNotificationsBell"), requireMessageParts(["admin"])]).then(
    ([module]) => ({ default: module.AdminNotificationsBell })
  )
);
const PartnerBell = lazy(() =>
  import("../components/PartnerAnnouncementsBell").then((module) => ({ default: module.PartnerAnnouncementsBell }))
);

type AdminBellProps = ComponentProps<typeof AdminBellComponent>;
type PartnerBellProps = ComponentProps<typeof PartnerBellComponent>;

export function AdminNotificationsBell(props: AdminBellProps) {
  return (
    <Suspense fallback={<BellPlaceholder />}>
      <AdminBell {...props} />
    </Suspense>
  );
}

export function PartnerAnnouncementsBell(props: PartnerBellProps) {
  return (
    <Suspense fallback={<BellPlaceholder />}>
      <PartnerBell {...props} />
    </Suspense>
  );
}
